import { useParams } from 'react-router';
import type { CSSProperties, ReactElement, RefObject } from 'react';

import { AppColors } from '@/config/appColors';
import { Endpoints } from '@/config/endpoints';
import type { PublicTablet } from '@/domain/models/publicTablet';
import { useL10n } from '@/l10n/useL10n';
import {
  verifyAdminTypeFromParam,
  type VerifyAdminType,
} from '@/presentation/pages/verifyAdmin/verifyAdminType';
import { BackgroundWrapper } from '@/presentation/widgets/BackgroundWrapper';
import { PrimaryIconButton } from '@/presentation/widgets/PrimaryIconButton';
import { PrimaryInputField } from '@/presentation/widgets/PrimaryInputField';
import { TabletPasswordPanel } from '@/presentation/widgets/TabletPasswordPanel';
import { formatCssColor, parseCssColor } from '@/utils/extensions/colorExtension';
import { wv } from '@/utils/screenUtil';

import { SchemeChip } from './SchemeChip';
import { ServerChip } from './ServerChip';
import { ServersChevron } from './ServersChevron';
import { TabletButton } from './TabletButton';
import { useBaseUrlController, type BaseUrlController } from './useBaseUrlController';
import styles from './BaseUrlPage.module.css';

/**
 * Перенос `lib/src/presentation/pages/base_url_page/base_url_page.dart` —
 * метод `build` и приватные виджеты страницы.
 *
 * Всё состояние экрана вынесено в `useBaseUrlController`: файл со
 * `_BaseUrlPageState` иначе не влез бы в разумный размер.
 */

/** `const columns = 3` */
const COLUMNS = 3;

/** `final spacing = 14.w` */
const SPACING = 14;

/**
 * `final tabletWidth = (_rowWidth.w - spacing * (columns - 1)) / columns`.
 *
 * Ширина считается от ряда (100% = `_rowWidth.w`), а не из `--sw`: три
 * карточки и два промежутка тогда укладываются в ряд ровно, без спора
 * о дробном пикселе между шириной ряда и суммой её частей.
 */
const TABLET_WIDTH = `calc((100% - var(--sw) * ${SPACING * (COLUMNS - 1)}) / ${COLUMNS})`;

/** `AppColors.cE94040.withValues(alpha: 0.5)` */
const CANCEL_BACKGROUND = formatCssColor({
  ...parseCssColor(AppColors.cE94040),
  alpha: 0.5,
});

/** `Gap(n.w)` — распорка в колонке. */
function Gap({ size }: { readonly size: number }): ReactElement {
  return (
    <div className={styles.gap} style={{ '--gap-size': wv(size) } as CSSProperties} />
  );
}

interface AddressRowProps {
  readonly value: string;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly scheme: string;
  readonly serversExpanded: boolean;
  readonly onChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly onToggleScheme: () => void;
  readonly onToggleServers: () => void;
  readonly onCancel: () => void;
}

/** Row(mainAxisSize: min, spacing: 40.w): крестик, поле адреса, отправка. */
function AddressRow(props: AddressRowProps): ReactElement {
  const { value, inputRef, scheme, serversExpanded } = props;
  const { onChange, onSubmit, onToggleScheme, onToggleServers, onCancel } = props;
  const l10n = useL10n();
  return (
    <div className={styles.row}>
      <PrimaryIconButton
        onPressed={onCancel}
        backgroundColor={CANCEL_BACKGROUND}
        icon="cancel"
      />
      <div className={styles.field}>
        <PrimaryInputField
          unfocusOnTapOutside={false}
          autoFocus
          hintText={l10n.enter_server_domain}
          value={value}
          onChange={onChange}
          inputRef={inputRef}
          // Домен, а не текст: без автокоррекции и подсказок,
          // иначе клавиатура планшета меняет «hron» и регистр.
          inputMode="url"
          enterKeyHint="go"
          autoCorrect={false}
          spellCheck={false}
          prefixIcon={<SchemeChip scheme={scheme} onTap={onToggleScheme} />}
          suffixIcon={
            <ServersChevron expanded={serversExpanded} onTap={onToggleServers} />
          }
          onFieldSubmitted={onSubmit}
        />
      </div>
      <PrimaryIconButton onPressed={onSubmit} icon="sendMessage" />
    </div>
  );
}

interface KnownServersProps {
  readonly servers: readonly string[];
  readonly onSelect: (root: string) => void;
  readonly onRemove: (root: string) => void;
}

/** Блок `if (_serversExpanded)`: Gap(20.w) и Wrap с запомненными серверами. */
function KnownServers({ servers, onSelect, onRemove }: KnownServersProps): ReactElement {
  return (
    <>
      <Gap size={20} />
      <div className={styles.wrap}>
        {servers.map((root) => (
          <ServerChip
            key={`known-server-${root}`}
            root={root}
            // Пресеты неудаляемы, стёртые руками вернутся после первого
            // успешного подключения.
            removable={!Endpoints.knownServerPresets.includes(root)}
            onTap={() => onSelect(root)}
            onRemove={() => onRemove(root)}
          />
        ))}
      </div>
    </>
  );
}

interface TabletsSectionProps {
  readonly tablets: readonly PublicTablet[];
  readonly isCurrent: (tablet: PublicTablet) => boolean;
  readonly onSelect: (tablet: PublicTablet) => void;
  readonly onAskPassword: (tablet: PublicTablet) => void;
}

/** Заголовок и Wrap со списком планшетов сервера. */
function TabletsSection(props: TabletsSectionProps): ReactElement {
  const { tablets, isCurrent, onSelect, onAskPassword } = props;
  const l10n = useL10n();
  return (
    <>
      <Gap size={32} />
      <span className={styles.sectionTitle}>
        {tablets.length === 0 ? l10n.server_has_no_tablets : l10n.choose_tablet}
      </span>
      <Gap size={20} />
      <div className={styles.wrap}>
        {tablets.map((tablet) => (
          <TabletButton
            key={`tablet-${tablet.slug}`}
            tablet={tablet}
            width={TABLET_WIDTH}
            isCurrent={isCurrent(tablet)}
            currentLabel={l10n.current_tablet}
            onTap={() => onSelect(tablet)}
            onLongPress={() => onAskPassword(tablet)}
          />
        ))}
      </div>
    </>
  );
}

/** Содержимое Column(mainAxisSize: min) — все блоки экрана по порядку. */
function BaseUrlBody({ page }: { readonly page: BaseUrlController }): ReactElement {
  const l10n = useL10n();
  const tablets = page.tablets;
  return (
    <>
      <AddressRow
        value={page.text}
        inputRef={page.inputRef}
        scheme={page.scheme}
        serversExpanded={page.serversExpanded}
        onChange={page.handleTextChanged}
        onSubmit={page.submit}
        onToggleScheme={page.toggleScheme}
        onToggleServers={page.toggleServers}
        onCancel={page.cancel}
      />
      {page.serversExpanded && (
        <KnownServers
          servers={page.knownServers}
          onSelect={page.selectKnownServer}
          onRemove={page.removeKnownServer}
        />
      )}
      {page.serverWithoutList && (
        <div className={styles.withoutList}>{l10n.server_without_tablet_list}</div>
      )}
      {tablets !== null && (
        <TabletsSection
          tablets={tablets}
          isCurrent={page.isCurrent}
          onSelect={page.selectTablet}
          onAskPassword={page.askTabletPassword}
        />
      )}
      {page.passwordTablet !== null && (
        // Панель пароля показывается только после long-press по планшету.
        <>
          <Gap size={32} />
          <TabletPasswordPanel
            title={page.passwordTablet.name}
            value={page.passwordText}
            onChange={page.handlePasswordChanged}
            onSubmit={page.retryConnect}
            onConnectWithoutPassword={page.connectWithoutAuth}
          />
        </>
      )}
    </>
  );
}

export interface BaseUrlPageProps {
  /**
   * `BaseUrlPage({required this.verifyAdminType})`. Необязателен: когда
   * роутер рендерит страницу без пропсов, тип берётся из адреса —
   * `/select-language/:verifyAdminType/baseUrl`.
   */
  readonly verifyAdminType?: VerifyAdminType;
}

export function BaseUrlPage({ verifyAdminType }: BaseUrlPageProps): ReactElement {
  const params = useParams();
  const page = useBaseUrlController(
    verifyAdminType ?? verifyAdminTypeFromParam(params.verifyAdminType),
  );

  return (
    <BackgroundWrapper>
      <div className={styles.root}>
        <div className={styles.scroll}>
          <div className={styles.column}>
            <BaseUrlBody page={page} />
          </div>
        </div>
      </div>
    </BackgroundWrapper>
  );
}
